import React, { useEffect, useMemo, useState } from 'react';
import { consultListDoctors, consultCreateAppointment, doctorListAppointments } from './api';
import { authFetch, authHeaders } from '../utils/authFetch';
import './ConsultModal.css';

const TIME_SLOTS = [
  '08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30',
  '16:00','16:30','17:00','17:30','18:00','18:30'
];

export default function ConsultModal({ open, onClose, onBooked }) {
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [purpose, setPurpose] = useState('Online consultation');
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState('');
  const [bookedTimes, setBookedTimes] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [address, setAddress] = useState('');
  const [contact, setContact] = useState('');
  const [age, setAge] = useState('');

  const patientFriendlyName = useMemo(() => {
    try {
      let display = (localStorage.getItem('neuro_display_name') || localStorage.getItem('neuro_user_name') || '').trim();
      if (display.includes('@')) {
        display = display.split('@')[0];
      }
      return display;
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const res = await consultListDoctors();
        if (res && res.status_code === 200) setDoctors(res.data || []);
        // Pre-fill age from logged-in user details if available
        try {
          const userName = localStorage.getItem('neuro_user_name');
          const userId = localStorage.getItem('neuro_user_id');
          if (userId || userName) {
            const base = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000' : 'https://app.neurolonic.com/api');
            const r = await authFetch(`${base}/user/details?${userId ? `user_id=${encodeURIComponent(userId)}` : `user_name=${encodeURIComponent(userName)}`}`, { headers: authHeaders() });
            const j = await r.json();
            const a = j?.data?.age;
            if (a) setAge(String(a));
          }
        } catch {}
      } catch (_) {
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!selectedDoctorId || !date) return;
    (async () => {
      try {
        const r = await doctorListAppointments(selectedDoctorId);
        if (r && r.status_code === 200) {
          const taken = (r.data || []).filter(a => a.date && a.time && a.date.startsWith(date)).map(a => a.time);
          setBookedTimes(taken);
        } else setBookedTimes([]);
      } catch { setBookedTimes([]); }
    })();
  }, [selectedDoctorId, date]);

  // Compute free slots, excluding already booked times and past times if date is today
  const freeSlots = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0,10);
    const now = new Date();
    const nowHHMM = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const isToday = date === todayStr;
    return TIME_SLOTS
      .filter(t => !bookedTimes.includes(t))
      .filter(t => !isToday || t > nowHHMM);
  }, [bookedTimes, date]);

  // If today has no available slots left, automatically shift to tomorrow
  useEffect(() => {
    try {
      if (!selectedDoctorId || !date) return;
      const todayStr = new Date().toISOString().slice(0,10);
      if (date !== todayStr) return;
      if (freeSlots.length > 0) return;
      const d = new Date();
      d.setDate(d.getDate() + 1);
      const tomorrow = d.toISOString().slice(0,10);
      setDate(tomorrow);
    } catch {}
  }, [selectedDoctorId, date, freeSlots]);

  const resolveDoctorDisplayName = (doctor) => {
    if (!doctor) return 'Doctor';
    const label = (doctor.display_name || doctor.user_name || '').trim();
    return label || 'Doctor';
  };

  async function handleBook(e) {
    e.preventDefault();
    setMessage('');
    setError('');
    
    const outpatient_user_id = localStorage.getItem('neuro_user_id');
    if (!outpatient_user_id) { 
      setError('Please login first.'); 
      return; 
    }
    
    if (!selectedDoctorId || !date || !selectedTimeSlot) { 
      setError('Please select doctor, date and time.'); 
      return; 
    }
    if (!address.trim() || !contact.trim()) {
      setError('Please provide address and phone number.');
      return;
    }
    
    try {
      setLoading(true);
      const patientDisplayName = (localStorage.getItem('neuro_display_name') || localStorage.getItem('neuro_user_name') || '').trim();
      const resp = await consultCreateAppointment({ 
        doctor_id: selectedDoctorId, 
        outpatient_user_id, 
        date, 
        time: selectedTimeSlot, 
        purpose, 
        duration_minutes: duration, 
        notes,
        age: age ? Number(age) : undefined,
        address: address.trim(),
        contact: contact.trim(),
        patient_name: patientDisplayName || undefined
      });
      
      if (resp && resp.status_code === 200) {
        setMessage('Appointment booked successfully!');
        try {
          const doctor = doctors.find(d => d.user_id === selectedDoctorId);
          const doctorDisplayName = resolveDoctorDisplayName(doctor);
          onBooked?.({
            doctorId: selectedDoctorId,
            doctorName: doctorDisplayName,
            date,
            time: selectedTimeSlot
          });
        } catch { }
        setTimeout(() => onClose && onClose(), 2000);
      } else {
        setError(resp?.message || 'Failed to book appointment');
      }
    } catch (e) {
      setError('Failed to book appointment');
    } finally { 
      setLoading(false); 
    }
  }

  if (!open) return null;
  
  return (
    <div className="consult-modal-overlay" onClick={onClose}>
      <div className="consult-modal-container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="consult-modal-header">
          <h2 className="consult-modal-title">Consult Doctor</h2>
          <p className="consult-modal-subtitle">Book an appointment with a mental health professional</p>
          {patientFriendlyName && (
            <div className="consult-patient-pill">
              Booking as <span>{patientFriendlyName}</span>
            </div>
          )}
          <button 
            type="button" 
            className="consult-modal-close" 
            aria-label="Close" 
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="consult-modal-body">
          {loading && (
            <div className="consult-loading">
              <div className="consult-spinner"></div>
              Loading doctors...
            </div>
          )}

          {error && (
            <div className="consult-error">
              {error}
            </div>
          )}

          {message && (
            <div className="consult-success">
              {message}
            </div>
          )}

          <form onSubmit={handleBook}>
            {/* Doctor Selection */}
            <div className="consult-section">
              <h3 className="consult-section-title">Select Doctor</h3>
              <div className="consult-doctors-grid">
                {doctors.map(doctor => {
                  const doctorDisplayName = resolveDoctorDisplayName(doctor);
                  return (
                  <div
                    key={doctor.user_id}
                    className={`consult-doctor-card ${selectedDoctorId === doctor.user_id ? 'selected' : ''}`}
                    onClick={() => setSelectedDoctorId(doctor.user_id)}
                  >
                    <div className="consult-doctor-name">{doctorDisplayName}</div>
                    <div className="consult-doctor-specialty">Mental Health Specialist</div>
                    <div className="consult-doctor-experience">Available for consultation</div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Date and Time Selection */}
            <div className="consult-section">
              <h3 className="consult-section-title">Schedule Appointment</h3>
              
              <div className="consult-form-group">
                <label className="consult-form-label">Date</label>
                <input
                  type="date"
                  className="consult-form-input"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  min={new Date().toISOString().slice(0,10)}
                  required
                />
              </div>

              {selectedDoctorId && date && (
                <div className="consult-form-group">
                  <label className="consult-form-label">Available Time Slots</label>
                  <div className="consult-time-slots">
                    {TIME_SLOTS.map(time => (
                      <div
                        key={time}
                        className={`consult-time-slot ${
                          bookedTimes.includes(time) ? 'booked' : 
                          selectedTimeSlot === time ? 'selected' : ''
                        }`}
                        onClick={() => !bookedTimes.includes(time) && setSelectedTimeSlot(time)}
                      >
                        {time}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Appointment Details */}
            <div className="consult-section">
              <h3 className="consult-section-title">Appointment Details</h3>
              
              <div className="consult-form-group">
                <label className="consult-form-label">Purpose</label>
                <input
                  type="text"
                  className="consult-form-input"
                  value={purpose}
                  onChange={e => setPurpose(e.target.value)}
                  placeholder="Brief description of consultation purpose"
                />
              </div>

              <div className="consult-form-group">
                <label className="consult-form-label">Duration</label>
                <select
                  className="consult-form-select"
                  value={duration}
                  onChange={e => setDuration(parseInt(e.target.value))}
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                </select>
              </div>

              <div className="consult-form-group">
                <label className="consult-form-label">Additional Notes (Optional)</label>
                <textarea
                  className="consult-form-input"
                  rows="3"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Any specific concerns or information you'd like to share..."
                />
              </div>
            </div>

            {/* Outpatient Info */}
            <div className="consult-section">
              <h3 className="consult-section-title">Your Details</h3>
              <div className="consult-form-grid-2">
                <div className="consult-form-group">
                  <label className="consult-form-label">Age</label>
                  <input
                    type="number"
                    className="consult-form-input"
                    value={age}
                    onChange={e => setAge(e.target.value)}
                    min={1}
                    max={120}
                    placeholder="Your age"
                  />
                </div>
                <div className="consult-form-group">
                  <label className="consult-form-label">Phone</label>
                  <input
                    type="tel"
                    className="consult-form-input"
                    value={contact}
                    onChange={e => setContact(e.target.value)}
                    placeholder="e.g., 9876543210"
                    required
                  />
                </div>
              </div>
              <div className="consult-form-group">
                <label className="consult-form-label">Address</label>
                <input
                  type="text"
                  className="consult-form-input"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="Street, City"
                  required
                />
              </div>
            </div>

            {/* Actions */}
            <div className="consult-modal-actions">
              <button
                type="button"
                className="consult-btn consult-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="consult-btn consult-btn-primary"
                disabled={loading || !selectedDoctorId || !selectedTimeSlot}
              >
                {loading ? 'Booking...' : 'Book Appointment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


